import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";

import { fetchEvents, fetchSpecimen, type CareEvent } from "../eventsApi";
import { CareGuide } from "../components/CareGuide";
import { History } from "../components/History";
import { QuickLog } from "../components/QuickLog";
import { UndoBar } from "../components/UndoBar";
import { LoggerProvider } from "../logger";
import { fetchProducts, type Product } from "../productsApi";
import { fetchPlan } from "../tasksApi";
import { photoFor } from "../photos";
import { flagsFor } from "../flags";
import { BackIcon } from "../icons";
import { PlantState } from "../components/PlantState";
import { WhereItLives } from "../components/WhereItLives";
import { Todos } from "../components/Todos";
import { ErrorState, LoadingState } from "../components/States";
import { LanguageToggle, useLanguage } from "../i18n";
import type { Specimen } from "../types";

/** Where it lives, and every fact about it that changes how it is cared for. */
function Flags({ plant }: { plant: Specimen }) {
  const { t, pick } = useLanguage();
  const flags = flagsFor(plant, t);
  return (
    <ul className="flags">
      <li className="flag">
        {plant.location.name.ar} · {pick(plant.location.name)} ·{" "}
        {plant.location.placement === "outdoor" ? t.outdoor : t.indoor}
      </li>
      {flags.map((flag) => (
        <li key={flag.label} className="flag" data-tone={flag.tone}>
          {flag.label}
        </li>
      ))}
    </ul>
  );
}

export function PlantPage() {
  const { t } = useLanguage();
  const { id = "" } = useParams();
  const [plant, setPlant] = useState<Specimen | null>(null);
  const [events, setEvents] = useState<CareEvent[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  // Whether the plan is currently warning that the guide cannot schedule this
  // plant where it is. Read from the plan rather than recomputed here, because
  // the guide's interval matrix lives on the server and inventing a second
  // opinion about it is how two answers start disagreeing.
  const [warned, setWarned] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(
    async (signal?: AbortSignal) => {
      try {
        const [found, history, allProducts, plan] = await Promise.all([
          fetchSpecimen(id, signal),
          fetchEvents(id, signal),
          fetchProducts(signal),
          fetchPlan(signal),
        ]);
        setPlant(found);
        setEvents(history);
        setProducts(allProducts);
        setWarned(plan.warnings.some((warning) => warning.specimen_id === id));
        setError(null);
      } catch (cause) {
        if (signal?.aborted) return;
        setError(cause instanceof Error ? cause.message : String(cause));
      } finally {
        setLoading(false);
      }
    },
    [id],
  );

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [load]);

  if (loading) return <main className="shell"><LoadingState /></main>;
  if (error || !plant) return <main className="shell"><ErrorState message={error ?? "Not found."} /></main>;

  // Arabic leads here too: it is what he calls the plant.
  const name = plant.nickname?.ar || plant.species.name.ar;
  const other = plant.species.name.en;
  const photo = photoFor(plant.species.scientific_name);

  return (
    <main className="shell">
      {/*
        Full-bleed, and the one place a photograph is allowed to be large:
        here it is evidence — what this plant looks like today — rather than an
        identifier in a list. The page colour is washed up into the bottom of
        the image so the name sits on the photograph itself.
      */}
      {photo ? (
        <div className="plant-hero">
          <img src={photo} alt="" />
          <p className="crumb">
            <Link className="nav-pill" to="/" aria-label={t.today}>
              <BackIcon />
            </Link>
            <LanguageToggle />
          </p>
          <header className="plant-head">
            <h1 lang="ar">{name}</h1>
            <p className="plant-en" lang="en" dir="ltr">{other}</p>
            <p className="binomial" dir="ltr">{plant.species.scientific_name}</p>
            <Flags plant={plant} />
          </header>
        </div>
      ) : (
        <>
          <p className="crumb">
            <Link className="nav-pill" to="/" aria-label={t.today}>
              <BackIcon />
            </Link>
            <LanguageToggle />
          </p>
          <header className="plant-head">
            <h1 lang="ar">{name}</h1>
            <p className="plant-en" lang="en" dir="ltr">{other}</p>
            <p className="binomial" dir="ltr">{plant.species.scientific_name}</p>
            <Flags plant={plant} />
          </header>
        </>
      )}

      {plant.care && <CareGuide care={plant.care} />}
      <LoggerProvider onCommitted={() => void load()}>
        <section className="logger">
          <h2>{t.recordWhatYouDid}</h2>
          <QuickLog specimenId={plant.id} plantName={name} products={products} />
        </section>
        {/* A move is a Care Event, so it lives inside the logger's grace
            window like every other log — and outside "How it is doing", which
            holds corrections to current state rather than things that happened. */}
        <WhereItLives plant={plant} events={events} warned={warned} />
        <UndoBar />
      </LoggerProvider>

      <PlantState plant={plant} onChanged={() => void load()} />
      <Todos specimenId={plant.id} />
      <History events={events} specimenId={plant.id} onCorrected={() => void load()} />
    </main>
  );
}
